// Copyright Amazon.com, Inc. or its affiliates. All Rights Reserved.
// SPDX-License-Identifier: Apache-2.0

import type { ImageAttachment } from "./types"

/**
 * Maps a browser file MIME type to the Bedrock image `format` the runtime
 * expects. Sólo están los formatos que acepta el modelo; cualquier otro MIME
 * resuelve a `undefined` y el archivo toma el carril de documento (el runtime
 * también rechaza formatos desconocidos del lado del servidor).
 *
 * Tiene que coincidir con `_IMAGE_FORMAT_BY_CT` del runtime y con la lista del
 * presign (`upload_presign/handler.py`), que es la que decide qué se puede subir.
 * `tests/infra/test_los_adjuntos_no_viajan_en_el_cuerpo_del_turno.py` fija esa
 * paridad.
 */
export const IMAGE_MIME_TO_FORMAT: Record<string, ImageAttachment["format"]> = {
  "image/png": "png",
  "image/jpeg": "jpeg",
  "image/webp": "webp",
  "image/gif": "gif",
}

/**
 * Same mapping keyed by file extension, used when the browser reports no (or an
 * unhelpful) MIME type.
 *
 * Why this exists: `file.type` is not guaranteed. Depending on OS, browser and
 * how the file was produced, a PNG can arrive as `""` or
 * `application/octet-stream`. Cuando la clasificación era sólo por MIME, un
 * archivo así no caía en ningún carril y se descartaba con un `logger.warning`:
 * el usuario recibía una respuesta segura calculada **sin su captura**, y la UI
 * no decía nada.
 *
 * Hoy los dos carriles van por S3, así que el formato ya no decide el transporte
 * — decide qué bloque construye el runtime (`image` vs `document`) y contra qué
 * techo de tamaño se mide el archivo (3.75 MB vs 4.5 MB).
 */
const IMAGE_EXT_TO_FORMAT: Record<string, ImageAttachment["format"]> = {
  png: "png",
  jpg: "jpeg",
  jpeg: "jpeg",
  webp: "webp",
  gif: "gif",
}

/** MIME types that carry no real information about the content. */
const OPAQUE_MIME_TYPES = new Set(["", "application/octet-stream", "binary/octet-stream"])

/**
 * Resolve the Bedrock image `format` for a picked file, or `undefined` if it is
 * not an image we support.
 *
 * MIME wins when it is meaningful; otherwise the extension decides. Es la única
 * fuente de verdad para «¿este archivo es una imagen?», y la usan el composer (la
 * etiqueta del chip), `attachmentBudget` (el techo de tamaño) y
 * `attachmentStatus` (la etiqueta de progreso). Si alguna clasifica por su cuenta,
 * vuelven a discrepar.
 */
export function imageFormatFor(file: { type?: string; name?: string }): ImageAttachment["format"] | undefined {
  const mime = (file.type || "").toLowerCase()
  const byMime = IMAGE_MIME_TO_FORMAT[mime]
  if (byMime) return byMime
  // Only fall back when the MIME is genuinely uninformative. A real MIME that we
  // don't support (e.g. `image/tiff`, `application/pdf`) must NOT be overridden by
  // a misleading extension.
  if (mime && !OPAQUE_MIME_TYPES.has(mime) && !mime.startsWith("image/")) return undefined
  const ext = (file.name || "").toLowerCase().split(".").pop() || ""
  return IMAGE_EXT_TO_FORMAT[ext]
}
